import { Link } from "react-router-dom";
import { Phone, AlertCircle } from "lucide-react";
import { subscribeNewsletter } from "../lib/api";
import { useState } from "react";
import { toast } from "sonner";

const FOOTER_LINKS = [
  ["/understanding", "Understanding the system"],
  ["/glossary", "Glossary"],
  ["/support", "Find support"],
  ["/stories", "Stories"],
  ["/journal", "Journal"],
  ["/about", "About this site"],
];

function CrisisBanner() {
  return (
    <div className="border-b border-white/10 bg-river-dark">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <AlertCircle
            size={20}
            strokeWidth={1.5}
            className="mt-0.5 flex-shrink-0 text-ember"
          />
          <p className="text-sm md:text-base font-accent text-linen/90">
            <strong className="font-semibold">
              If a child is in immediate danger
            </strong>
            , call <span className="whitespace-nowrap">999</span>. For
            non-emergency concerns, call the NSPCC on{" "}
            <span className="whitespace-nowrap">0808 800 5000</span> or the
            Samaritans on <span className="whitespace-nowrap">116 123</span>.
          </p>
        </div>
        <Phone
          size={20}
          strokeWidth={1.5}
          className="hidden md:block text-linen/40"
        />
      </div>
    </div>
  );
}

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      setDone(true);
      setEmail("");
    } catch (err) {
      toast.error("Sorry, something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="newsletter-success"
        className="mt-5 border border-ember/40 bg-ember/10 px-5 py-4"
      >
        <div className="text-ember font-accent text-xs uppercase tracking-[0.18em] font-semibold">
          You&rsquo;re subscribed
        </div>
        <p className="mt-2 text-linen/90 text-sm leading-relaxed">
          Thank you. We&rsquo;ll only write occasionally — campaign news, new
          explainers, and stories worth sharing.
        </p>
        <button
          type="button"
          data-testid="newsletter-add-another-button"
          onClick={() => setDone(false)}
          className="mt-3 text-xs text-linen/60 hover:text-linen font-accent underline underline-offset-4"
        >
          Add another email
        </button>
      </div>
    );
  }

  return (
    <form
      data-testid="newsletter-form"
      onSubmit={onSubmit}
      className="mt-5 flex flex-col sm:flex-row gap-2"
    >
      <label htmlFor="newsletter-email" className="sr-only">
        Email address
      </label>
      <input
        id="newsletter-email"
        name="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your@email.com"
        data-testid="newsletter-email-input"
        className="flex-1 bg-transparent border border-linen/20 px-4 py-3 text-sm text-linen placeholder:text-linen/40 focus:outline-none focus:border-ember"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid="newsletter-submit-button"
        className="bg-ember text-ink px-5 py-3 text-sm font-accent font-semibold tracking-wide hover:bg-ember/90 transition-colors disabled:opacity-60"
      >
        {loading ? "Sending…" : "Subscribe"}
      </button>
    </form>
  );
}

function FooterIntro() {
  return (
    <div className="md:col-span-5">
      <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent">
        You Are Not Alone
      </div>
      <h3 className="font-serif text-3xl md:text-4xl mt-3 leading-[1.1] text-linen text-balance">
        A calm starting point, in a process that rarely feels calm.
      </h3>
      <p className="mt-5 text-linen/70 leading-relaxed max-w-md">
        We are an independent voice for families in the child protection system
        in England and Wales. This site offers plain-English explanations,
        signposting to qualified support, and a campaign for a more open,
        accountable system.
      </p>
      <div className="mt-6 space-y-1 text-sm" data-testid="footer-emails">
        {[
          ["info@truth4parents.com", "General"],
          ["press@truth4parents.com", "Press"],
          ["support@truth4parents.com", "Families"],
        ].map(([addr, label]) => (
          <div key={addr} className="flex items-baseline gap-3">
            <span className="text-linen/40 font-accent text-xs uppercase tracking-wide w-16">{label}</span>
            <a
              href={`mailto:${addr}`}
              className="text-linen/80 hover:text-ember transition-colors font-accent"
            >
              {addr}
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function FooterLinks() {
  return (
    <div className="md:col-span-3">
      <div className="text-xs uppercase tracking-[0.22em] text-linen/40 font-accent">
        The site
      </div>
      <ul className="mt-4 space-y-3">
        {FOOTER_LINKS.map(([to, label]) => (
          <li key={to}>
            <Link
              to={to}
              data-testid={`footer-${to.replace("/", "")}-link`}
              className="text-linen/80 hover:text-ember transition-colors text-sm font-accent"
            >
              {label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function FooterLegal() {
  return (
    <>
      <div className="mt-16 pt-10 border-t border-white/10 text-center">
        <p
          data-testid="footer-tagline"
          className="font-serif italic text-xl md:text-2xl text-linen/90 leading-snug"
        >
          Trust and respect are earned, not demanded.
        </p>
      </div>
      <div className="mt-10 pt-8 border-t border-white/10 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-xs text-linen/50 font-accent">
        <div className="max-w-2xl">
          <strong className="text-linen/80">This site is not legal advice.</strong>{" "}
          The information here is general and should not replace advice from a
          qualified solicitor or advocate. If you are facing a hearing or
          assessment, please seek specialist legal help — for parents in care
          proceedings in England and Wales, legal aid is free and
          non-means-tested.
        </div>
        <div className="flex flex-col md:items-end gap-1 flex-shrink-0">
          <Link
            to="/privacy"
            data-testid="footer-privacy-link"
            className="text-linen/60 hover:text-ember transition-colors"
          >
            Privacy
          </Link>
          <div>© {new Date().getFullYear()} You Are Not Alone</div>
        </div>
      </div>
    </>
  );
}

export const Footer = () => (
  <footer data-testid="site-footer" className="bg-ink text-linen mt-24">
    <CrisisBanner />
    <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 py-16">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
        <FooterIntro />
        <FooterLinks />
        <div className="md:col-span-4">
          <div className="text-xs uppercase tracking-[0.22em] text-linen/40 font-accent">
            Stay in touch
          </div>
          <p className="mt-4 text-sm text-linen/70 leading-relaxed">
            Occasional updates — campaign news, new explainers, and stories we
            are asked to share. No noise, no spam.
          </p>
          <NewsletterForm />
        </div>
      </div>
      <FooterLegal />
    </div>
  </footer>
);

export default Footer;
